import { useState, useCallback, useEffect } from "react"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import { onAuthStateChange, signInWithEmail, signOut } from "@/lib/supabase/auth"
import type { User as AppUser } from "@/lib/supabase/types"

interface AdminInfo {
  id: string
  email: string
  nickname: string
  avatar_url: string | null
  role: "admin" | "super_admin"
}

interface AdminState {
  admin: AdminInfo | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

export function useAdminStore() {
  const [state, setState] = useState<AdminState>({
    admin: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  })

  useEffect(() => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) {
      setState((s) => ({ ...s, isLoading: false }))
      return
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        loadAdminProfile(session.user.id, session.user.email)
      } else {
        setState({ admin: null, isAuthenticated: false, isLoading: false, error: null })
      }
    })

    const unsub = onAuthStateChange(supabase, (_event, user) => {
      if (user) {
        loadAdminProfile(user.id, user.email ?? "")
      } else {
        setState({ admin: null, isAuthenticated: false, isLoading: false, error: null })
      }
    })

    return () => {
      unsub.unsubscribe()
    }
  }, [])

  const loadAdminProfile = useCallback(async (userId: string, email: string | undefined) => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) {
      setState({ admin: null, isAuthenticated: false, isLoading: false, error: null })
      return
    }

    try {
      const { data: profile } = await supabase
        .from("users")
        .select("id, email, nickname, avatar_url, role")
        .eq("id", userId)
        .single()

      const roleTags: string[] = ((profile as AppUser | null)?.role ?? "").split(",").filter(Boolean)
      const isAdmin = roleTags.includes("admin") || roleTags.includes("super_admin")

      if (!isAdmin) {
        setState({ admin: null, isAuthenticated: false, isLoading: false, error: "该用户没有管理员权限" })
        return
      }

      setState({
        admin: {
          id: userId,
          email: email ?? "",
          nickname: (profile as AppUser | null)?.nickname ?? email ?? "",
          avatar_url: (profile as AppUser | null)?.avatar_url ?? null,
          role: roleTags.includes("super_admin") ? "super_admin" : "admin" as const,
        },
        isAuthenticated: true,
        isLoading: false,
        error: null,
      })
    } catch {
      setState({ admin: null, isAuthenticated: false, isLoading: false, error: "加载管理员资料失败" })
    }
  }, [])

  const login = useCallback(async (email: string, password: string): Promise<string | null> => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) return "数据库未配置"

    setState((s) => ({ ...s, isLoading: true, error: null }))

    const { error, user } = await signInWithEmail(supabase, { email, password })
    if (error) {
      setState((s) => ({ ...s, isLoading: false, error }))
      return error
    }

    // 认证成功，等待管理员权限检查完成
    if (user) {
      return new Promise<string | null>((resolve) => {
        let resolved = false
        const timer = setTimeout(() => {
          if (!resolved) {
            resolved = true
            resolve(null)
          }
        }, 5000)

        const checkResult = () => {
          if (resolved) return
          setState((prev) => {
            if (prev.isAuthenticated) {
              resolved = true
              clearTimeout(timer)
              resolve(null)
            } else if (prev.error) {
              resolved = true
              clearTimeout(timer)
              resolve(prev.error)
            }
            return prev
          })
        }

        // 轮询等待 loadAdminProfile 完成
        const interval = setInterval(() => {
          checkResult()
          if (resolved) clearInterval(interval)
        }, 100)
      })
    }

    return null
  }, [])

  const logout = useCallback(async () => {
    const supabase = getBrowserSupabaseClient()
    if (supabase) {
      await signOut(supabase)
    }
    setState({ admin: null, isAuthenticated: false, isLoading: false, error: null })
  }, [])

  return {
    ...state,
    login,
    logout,
  }
}
